import type { AppEnvironment } from "./app-environment";

const BEACON_URL = "https://static.cloudflareinsights.com/beacon.min.js";
const OPT_OUT_KEY = "yarn-analytics-off";

export interface AnalyticsScript {
  src: string;
  /** Cloudflare reads its site token from this attribute. */
  beaconConfig: string;
}

/** Only production counts, so local use, staging, and test runs stay out of the numbers. */
export function analyticsScriptFor(
  environment: AppEnvironment,
  token: string | undefined,
): AnalyticsScript | null {
  if (environment !== "production" || !token) {
    return null;
  }
  return { src: BEACON_URL, beaconConfig: JSON.stringify({ token }) };
}

/**
 * Keeps our own visits out of production's numbers. Automated browsers never count.
 * `?analytics=off` opts this browser out for good, and `?analytics=on` undoes it.
 */
export function isOwnVisit(
  search: string,
  storage: Pick<Storage, "getItem" | "setItem" | "removeItem">,
  automated: boolean,
): boolean {
  const choice = new URLSearchParams(search).get("analytics");
  if (choice === "off") {
    storage.setItem(OPT_OUT_KEY, "1");
  } else if (choice === "on") {
    storage.removeItem(OPT_OUT_KEY);
  }
  return automated || storage.getItem(OPT_OUT_KEY) !== null;
}

export function startAnalytics(environment: AppEnvironment, token: string | undefined): void {
  const config = analyticsScriptFor(environment, token);
  if (config === null || ownVisitNow()) {
    return;
  }
  const script = document.createElement("script");
  script.type = "module";
  script.src = config.src;
  script.dataset.cfBeacon = config.beaconConfig;
  document.head.append(script);
}

function ownVisitNow(): boolean {
  try {
    return isOwnVisit(window.location.search, localStorage, navigator.webdriver);
  } catch {
    // Blocked storage: an ordinary visitor, unless it is automated.
    return navigator.webdriver;
  }
}
